import React, { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import MenuButton from "../MenuButton/MenuButton";
import { NoteState } from "../../context/notes/NoteState";
import UserServices from "../../UserServices/UserServices";
import SocialLeadServices from "../../UserServices/SocialLeadServices";
import {
  canUseSocialLeadAllBranches,
  filterSocialLeadBranches,
} from "../../utils/branchAccess";
import "./SocialLeads.css";

const TODAY = () => new Date().toLocaleDateString("en-CA");

const daysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toLocaleDateString("en-CA");
};

/** Normalize API dates to YYYY-MM-DD (avoids UTC ISO day-shift). */
const formatLeadDate = (value) => {
  if (!value) return "";
  const s = String(value);
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d.toLocaleDateString("en-CA");
  return s.slice(0, 10);
};

/** Intake — Social Media Manager (core lead details) */
const INTAKE_FIELDS = [
  "branch_id",
  "lead_date",
  "customer_name",
  "contact_number",
  "member_responsible",
  "member_responsible_id",
  "contact_medium",
  "offer",
  "whatsapp_detail",
  "comment",
];

/**
 * Follow-up — Sales
 * Also includes member_responsible + comment so sales can assign and note updates.
 */
const SALES_FIELDS = [
  "member_responsible",
  "member_responsible_id",
  "comment",
  "first_call_answered",
  "interested",
  "not_interested",
  "follow_up_calls",
  "visit_scheduled",
  "visit_completed",
  "second_follow_up",
  "call_not_answered",
  "whatsapp_message_dropped",
  "payment_received",
  "closed_by",
  "follow_up_comment",
  "first_follow_up_note",
  "first_follow_up_response",
  "second_follow_up_note",
  "second_follow_up_response",
];

const INTAKE_FLAGS = [{ key: "whatsapp_detail", label: "WhatsApp detail" }];

const SALES_FLAGS = [
  { key: "first_call_answered", label: "First call answered?" },
  { key: "call_not_answered", label: "Not answered" },
  { key: "whatsapp_message_dropped", label: "Message dropped on WhatsApp" },
  { key: "interested", label: "Interested?" },
  { key: "not_interested", label: "Not interested?" },
  { key: "follow_up_calls", label: "Follow up calls? (2)" },
  { key: "visit_scheduled", label: "Visit scheduled?" },
  { key: "visit_completed", label: "Visit completed?" },
  { key: "second_follow_up", label: "2nd follow up (visitor)" },
  { key: "payment_received", label: "Payment received & orientation" },
];

/** Paid leads are closed. Follow-up outcomes can sit beside hot or cold. */
const leadStatusTags = (lead) => {
  if (lead?.payment_received) {
    return [{ label: "Closed", tone: "green" }];
  }
  const tags = [];
  if (lead?.not_interested) tags.push({ label: "Cold", tone: "red" });
  else if (lead?.interested) tags.push({ label: "Hot", tone: "green" });
  if (lead?.call_not_answered) tags.push({ label: "Not answered", tone: "amber" });
  if (lead?.whatsapp_message_dropped) tags.push({ label: "WhatsApp", tone: "blue" });
  if (!tags.length) tags.push({ label: "Opened", tone: "yellow" });
  return tags;
};

const commentLines = (lead) => {
  const lines = [];
  if (lead?.comment) lines.push(["Intake", lead.comment]);
  if (lead?.follow_up_comment) lines.push(["Sales", lead.follow_up_comment]);
  if (lead?.first_follow_up_note || lead?.first_follow_up_response) {
    lines.push(["1st follow up", lead.first_follow_up_note || "—"]);
    lines.push(["Response", lead.first_follow_up_response || "—"]);
  }
  if (lead?.second_follow_up_note || lead?.second_follow_up_response) {
    lines.push(["2nd follow up", lead.second_follow_up_note || "—"]);
    lines.push(["Response", lead.second_follow_up_response || "—"]);
  }
  return lines;
};

const StatusTag = ({ lead }) => (
  <>
    {leadStatusTags(lead).map((tag) => (
      <span key={tag.label} className={`sl-status-tag sl-status-${tag.tone}`}>
        {tag.label}
      </span>
    ))}
  </>
);

/** Progress steps (excludes "not interested"; paid = 100%) */
const PROGRESS_FLAGS = [
  "whatsapp_detail",
  "first_call_answered",
  "interested",
  "follow_up_calls",
  "visit_scheduled",
  "visit_completed",
  "second_follow_up",
  "payment_received",
];

const calcProgressPct = (source) => {
  if (source?.payment_received) return 100;
  const done = PROGRESS_FLAGS.filter((k) => !!source?.[k]).length;
  return Math.round((done / PROGRESS_FLAGS.length) * 100);
};

const emptyForm = (branchId) => ({
  branch_id: branchId || "",
  lead_date: TODAY(),
  customer_name: "",
  contact_number: "",
  member_responsible: "",
  member_responsible_id: "",
  contact_medium: "",
  offer: "",
  whatsapp_detail: false,
  first_call_answered: false,
  call_not_answered: false,
  whatsapp_message_dropped: false,
  interested: false,
  not_interested: false,
  follow_up_calls: false,
  visit_scheduled: false,
  visit_completed: false,
  second_follow_up: false,
  payment_received: false,
  closed_by: "",
  comment: "",
  follow_up_comment: "",
  first_follow_up_note: "",
  first_follow_up_response: "",
  second_follow_up_note: "",
  second_follow_up_response: "",
});

const QUEUES = [
  { key: "", label: "All", stat: "total_leads", hint: "Every lead in the date range" },
  { key: "untouched", label: "Untouched", stat: "untouched", hint: "No call, no follow-up, not marked hot or cold" },
  { key: "not_answered", label: "Not answered", stat: "call_not_answered", hint: "Called, but nobody picked up" },
  { key: "whatsapp_dropped", label: "WhatsApp message", stat: "whatsapp_message_dropped", hint: "A WhatsApp message was dropped" },
  { key: "first_follow_up", label: "1st follow up", stat: "first_follow_up", hint: "Contacted, first follow-up not recorded yet" },
  { key: "second_follow_up", label: "2nd follow up", stat: "second_follow_up", hint: "First follow-up done, second not recorded yet" },
  { key: "hot", label: "Hot leads", stat: "hot", hint: "Interested and not paid yet" },
  { key: "cold", label: "Cold leads", stat: "cold", hint: "Marked not interested" },
];

function CustomSelect({ label, value, options, onChange, disabled }) {
  const isCustom =
    value &&
    value !== "" &&
    !options.includes(value) &&
    value !== "__custom__";
  const [mode, setMode] = useState(isCustom ? "__custom__" : value || "");
  const [customText, setCustomText] = useState(isCustom ? value : "");

  useEffect(() => {
    if (isCustom) {
      setMode("__custom__");
      setCustomText(value);
    } else {
      setMode(value || "");
    }
  }, [value, isCustom]);

  return (
    <div className="form-group mb-2">
      <label className="form-label small mb-1">{label}</label>
      <select
        className="form-select form-select-sm"
        value={mode === "__custom__" || isCustom ? "__custom__" : mode}
        disabled={disabled}
        onChange={(e) => {
          const v = e.target.value;
          if (v === "__custom__") {
            setMode("__custom__");
            onChange(customText || "");
          } else {
            setMode(v);
            setCustomText("");
            onChange(v);
          }
        }}
      >
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
        <option value="__custom__">Custom (type your own)…</option>
      </select>
      {(mode === "__custom__" || isCustom) && !disabled ? (
        <input
          className="form-control form-control-sm mt-1"
          placeholder="Enter custom value"
          value={customText}
          onChange={(e) => {
            setCustomText(e.target.value);
            onChange(e.target.value);
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Shared Social Leads page.
 * Role 16 (SMM): create + edit intake fields
 * Role 4 (Sales): edit follow-up fields on existing leads
 * Roles 1/3 (Admin): full access
 */
export default function SocialLeads({ salesMode = false }) {
  const { state } = useContext(NoteState);
  const { branch_id, role } = state;
  const roleStr = String(role || "");

  const isAdmin = roleStr === "1" || roleStr === "3";
  const isSmm = roleStr === "16";
  const isSales = roleStr === "4";
  const canPickBothGyms = canUseSocialLeadAllBranches(roleStr);

  const canEditIntake = isAdmin || isSmm;
  const canEditSales = isAdmin || isSales;
  /** Sales may assign member responsible + comments on existing leads */
  const canEditAssignment = canEditIntake || canEditSales;
  const canCreate = canEditIntake;
  const canDelete = isAdmin || isSmm;
  const canSave = canEditIntake || canEditSales;

  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(
    canPickBothGyms ? "" : branch_id || ""
  );
  const [fromDate, setFromDate] = useState(daysAgo(4));
  const [toDate, setToDate] = useState(TODAY());
  const [queue, setQueue] = useState("");
  const [allDates, setAllDates] = useState(false);
  const [offerFilter, setOfferFilter] = useState("");
  const [search, setSearch] = useState("");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [options, setOptions] = useState({
    contact_medium: [],
    offer: [],
    member_responsible: [],
    member_responsible_staff: [],
    closed_by: [],
  });
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm(branch_id));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (canPickBothGyms) {
      setSelectedBranch((current) => {
        if (!current || String(current) === String(branch_id ?? "")) {
          return "";
        }
        return current;
      });
      return;
    }
    setSelectedBranch(branch_id || "");
    setForm((f) => ({ ...f, branch_id: branch_id || f.branch_id }));
  }, [branch_id, canPickBothGyms]);

  useEffect(() => {
    const request = canPickBothGyms
      ? UserServices.branchNameAll()
      : UserServices.branchName();
    request
      .then((res) => {
        const list = res.data?.data || [];
        const next = Array.isArray(list) ? list : [];
        setBranches(
          canPickBothGyms ? filterSocialLeadBranches(next) : next
        );
      })
      .catch(() => setBranches([]));
  }, [canPickBothGyms]);

  const loadOptions = async (bId) => {
    try {
      const res = await SocialLeadServices.getOptions(bId || undefined);
      setOptions(res.data?.data || {});
    } catch (e) {
      /* ignore */
    }
  };

  const buildFilterParams = (overrides = {}) => {
    const useAllDates =
      overrides.allDates !== undefined ? overrides.allDates : allDates;
    const useFrom =
      overrides.fromDate !== undefined ? overrides.fromDate : fromDate;
    const useTo = overrides.toDate !== undefined ? overrides.toDate : toDate;
    const useOffer =
      overrides.offerFilter !== undefined ? overrides.offerFilter : offerFilter;
    const useSearch =
      overrides.search !== undefined ? overrides.search : search;
    const useQueue = overrides.queue !== undefined ? overrides.queue : queue;

    const params = {
      branch_id: selectedBranch || undefined,
      offer: useOffer || undefined,
      search: useSearch || undefined,
      queue: useQueue || undefined,
      per_page: 500,
    };

    if (useAllDates) {
      params.all_dates = 1;
    } else {
      if (useFrom) params.from_date = useFrom;
      if (useTo) params.to_date = useTo;
      if (useFrom && useTo && useFrom === useTo) {
        params.date = useFrom;
      }
    }

    return params;
  };

  const loadData = async (overrides = {}) => {
    setLoading(true);
    try {
      const params = buildFilterParams(overrides);
      const statsParams = { ...params };
      delete statsParams.per_page;

      const [leadsRes, statsRes] = await Promise.all([
        SocialLeadServices.getLeads(params),
        SocialLeadServices.getStats(statsParams),
      ]);
      const page = leadsRes.data?.data;
      setLeads(page?.data || page || []);
      setStats(statsRes.data?.data || null);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to load leads";
      toast.error(
        err.response?.status === 500
          ? `${msg} (Check social_leads tables / migration on server)`
          : msg
      );
      setLeads([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOptions(selectedBranch);
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm(selectedBranch || (canPickBothGyms ? "" : branch_id)));
    setShowForm(true);
  };

  const openEdit = (lead) => {
    setEditingId(lead.id);
    setForm({
      branch_id: lead.branch_id || "",
      lead_date: formatLeadDate(lead.lead_date) || TODAY(),
      customer_name: lead.customer_name || "",
      contact_number: lead.contact_number || "",
      member_responsible: lead.member_responsible || "",
      member_responsible_id: lead.member_responsible_id || "",
      contact_medium: lead.contact_medium || "",
      offer: lead.offer || "",
      whatsapp_detail: !!lead.whatsapp_detail,
      first_call_answered: !!lead.first_call_answered,
      call_not_answered: !!lead.call_not_answered,
      whatsapp_message_dropped: !!lead.whatsapp_message_dropped,
      interested: !!lead.interested,
      not_interested: !!lead.not_interested,
      follow_up_calls: !!lead.follow_up_calls,
      visit_scheduled: !!lead.visit_scheduled,
      visit_completed: !!lead.visit_completed,
      second_follow_up: !!lead.second_follow_up,
      payment_received: !!lead.payment_received,
      closed_by: lead.closed_by || "",
      comment: lead.comment || "",
      follow_up_comment: lead.follow_up_comment || "",
      first_follow_up_note: lead.first_follow_up_note || "",
      first_follow_up_response: lead.first_follow_up_response || "",
      second_follow_up_note: lead.second_follow_up_note || "",
      second_follow_up_response: lead.second_follow_up_response || "",
    });
    setShowForm(true);
  };

  const buildPayload = () => {
    const keys = editingId
      ? [
          ...(canEditIntake ? INTAKE_FIELDS : []),
          ...(canEditSales ? SALES_FIELDS : []),
        ]
      : INTAKE_FIELDS;

    const payload = {};
    keys.forEach((k) => {
      if (k === "branch_id") {
        payload.branch_id = form.branch_id ? parseInt(form.branch_id, 10) : null;
      } else if (k === "member_responsible_id") {
        payload.member_responsible_id = form.member_responsible_id
          ? parseInt(form.member_responsible_id, 10)
          : null;
      } else {
        payload[k] = form[k];
      }
    });
    return payload;
  };

  const saveLead = async (e) => {
    e.preventDefault();
    if (!canSave) return;

    if (!editingId && canCreate) {
      if (!form.customer_name || !form.branch_id || !form.lead_date) {
        toast.error("Branch, date and customer name are required");
        return;
      }
    }

    setSaving(true);
    try {
      const payload = buildPayload();
      if (editingId) {
        await SocialLeadServices.updateLead(editingId, payload);
        toast.success("Lead updated");
      } else {
        await SocialLeadServices.createLead(payload);
        toast.success("Lead added");
      }
      setShowForm(false);
      await loadOptions(form.branch_id || selectedBranch);
      await loadData();
    } catch (err) {
      const data = err.response?.data;
      const msg =
        (typeof data?.message === "string" && data.message) ||
        data?.message?.customer_name?.[0] ||
        data?.message?.branch_id?.[0] ||
        data?.message?.lead_date?.[0] ||
        (data?.error && String(data.error)) ||
        "Save failed";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const removeLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await SocialLeadServices.deleteLead(id);
      toast.success("Lead deleted");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    }
  };

  const rowClass = (lead) => {
    if (lead.payment_received || lead.visit_completed) return "sl-row-success";
    if (lead.not_interested) return "sl-row-danger";
    if (lead.interested || lead.visit_scheduled) return "sl-row-info";
    return "";
  };

  const title = salesMode || isSales
    ? "Social Leads — Sales follow-up"
    : "Social Media Leads";

  const subtitle = isSales
    ? "Record not answered, a WhatsApp message, and the 1st and 2nd follow-up. Those counts sit with the other filters above the list."
    : isSmm
      ? "Add new leads (name, contact, offer). Sales records each follow-up and the response."
      : "Full access: intake, follow-up notes, and the response on each call.";

  const progressPct = useMemo(() => calcProgressPct(form), [form]);

  const setMemberResponsible = (name) => {
    const staff = options.member_responsible_staff || [];
    const match = staff.find(
      (s) => String(s.name).trim().toLowerCase() === String(name || "").trim().toLowerCase()
    );
    setForm((prev) => ({
      ...prev,
      member_responsible: name || "",
      member_responsible_id: match?.id || "",
    }));
  };

  const formTitle = !editingId
    ? "Add lead"
    : isSales && !isAdmin
      ? "Update sales follow-up"
      : isSmm && !isAdmin
        ? "Edit lead intake"
        : "Edit lead";

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          <div className="social-leads-page">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div>
              <h4 className="mb-0">{title}</h4>
              <p className="small text-muted mb-0">{subtitle}</p>
            </div>
            {canCreate ? (
              <button
                type="button"
                className="btn btn-sm btn-primary sl-add-btn"
                onClick={openCreate}
              >
                + Add lead
              </button>
            ) : null}
          </div>

          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body py-2">
              <div className="row g-2 align-items-end">
                <div className="col-6 col-md-2">
                  <label className="form-label small mb-1">Branch</label>
                  <select
                    className="form-select form-select-sm"
                    value={selectedBranch}
                    onChange={(e) => setSelectedBranch(e.target.value)}
                  >
                    <option value="">All branches</option>
                    {branches.map((b) => (
                      <option
                        key={b.id || b.branch_id}
                        value={b.id || b.branch_id}
                      >
                        {b.name || b.branch_name}
                      </option>
                    ))}
                    {!branches.length && selectedBranch ? (
                      <option value={selectedBranch}>Current branch</option>
                    ) : null}
                  </select>
                </div>
                <div className="col-6 col-md-2">
                  <label className="form-label small mb-1">From date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={fromDate}
                    disabled={allDates}
                    onChange={(e) => {
                      setAllDates(false);
                      setFromDate(e.target.value);
                    }}
                  />
                </div>
                <div className="col-6 col-md-2">
                  <label className="form-label small mb-1">To date</label>
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={toDate}
                    disabled={allDates}
                    onChange={(e) => {
                      setAllDates(false);
                      setToDate(e.target.value);
                    }}
                  />
                </div>
                <div className="col-6 col-md-2">
                  <label className="form-label small mb-1">Offer</label>
                  <select
                    className="form-select form-select-sm"
                    value={offerFilter}
                    onChange={(e) => setOfferFilter(e.target.value)}
                  >
                    <option value="">All offers</option>
                    {(options.offer || []).map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-6 col-md-2">
                  <label className="form-label small mb-1">Search</label>
                  <input
                    className="form-control form-control-sm"
                    placeholder="Name / phone"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && loadData()}
                  />
                </div>
                <div className="col-6 col-md-2 d-flex gap-1">
                  <button
                    type="button"
                    className="btn btn-sm btn-primary flex-grow-1"
                    onClick={loadData}
                    disabled={loading}
                  >
                    {loading ? "Loading…" : "Apply"}
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm text-nowrap ${
                      allDates ? "btn-secondary" : "btn-outline-secondary"
                    }`}
                    title="Show all dates"
                    onClick={() => {
                      setAllDates(true);
                      setFromDate("");
                      setToDate("");
                      loadData({
                        allDates: true,
                        fromDate: "",
                        toDate: "",
                      });
                    }}
                  >
                    All
                  </button>
                </div>
              </div>
              {allDates ? (
                <div className="small text-muted mt-2">
                  Showing all dates
                  {offerFilter ? ` · Offer: ${offerFilter}` : ""}.
                </div>
              ) : fromDate || toDate ? (
                <div className="small text-muted mt-2">
                  Last 5 days by default: {fromDate || "…"} → {toDate || "…"}
                  {offerFilter ? ` · Offer: ${offerFilter}` : ""}.
                </div>
              ) : null}
            </div>
          </div>

          {stats ? (
            <div className="sl-stats mb-3">
              <div className="sl-stat">
                <span>Total leads</span>
                <strong>{stats.total_leads}</strong>
              </div>
              <div className="sl-stat">
                <span>Answered</span>
                <strong>{stats.first_call_answered}</strong>
              </div>
              <div className="sl-stat">
                <span>Not answered</span>
                <strong>{stats.call_not_answered ?? 0}</strong>
              </div>
              <div className="sl-stat">
                <span>WhatsApp message</span>
                <strong>{stats.whatsapp_message_dropped ?? 0}</strong>
              </div>
              <div className="sl-stat">
                <span>Interested</span>
                <strong>{stats.interested}</strong>
              </div>
              <div className="sl-stat">
                <span>Not interested</span>
                <strong>{stats.not_interested}</strong>
              </div>
              <div className="sl-stat">
                <span>Visits done</span>
                <strong>{stats.visit_completed}</strong>
              </div>
              <div className="sl-stat">
                <span>Converted (Paid)</span>
                <strong>{stats.converted ?? stats.payment_received}</strong>
              </div>
              <div className="sl-stat">
                <span>Conversion %</span>
                <strong>{stats.conversion_rate ?? 0}%</strong>
              </div>
            </div>
          ) : null}

          <div className="sl-queues mb-3">
            {QUEUES.map((item) => (
              <button
                key={item.key || "all"}
                type="button"
                title={item.hint}
                className={`sl-queue-btn${queue === item.key ? " is-active" : ""}`}
                onClick={() => {
                  setQueue(item.key);
                  loadData({ queue: item.key });
                }}
              >
                <span>{item.label}</span>
                <strong>{stats ? stats[item.stat] ?? 0 : "—"}</strong>
              </button>
            ))}
          </div>

          {stats && Array.isArray(stats.by_offer) && stats.by_offer.length ? (
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body py-2">
                <div className="small text-muted mb-2">
                  Offer-wise (leads → converted)
                </div>
                <div className="table-responsive">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th>Offer</th>
                        <th className="text-end">Leads</th>
                        <th className="text-end">Converted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.by_offer.map((row) => (
                        <tr key={row.offer}>
                          <td>{row.offer}</td>
                          <td className="text-end">{row.total}</td>
                          <td className="text-end">{row.converted}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : null}

          <div className="table-responsive card border-0 shadow-sm">
            <table className="table table-sm table-hover mb-0 sl-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Contact</th>
                  <th>Branch</th>
                  <th>Responsible</th>
                  <th>Medium</th>
                  <th>Offer</th>
                  <th>Comments</th>
                  <th>Progress</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="text-center py-5">
                      {loading ? (
                        <span className="text-muted">Loading leads…</span>
                      ) : (
                        <div className="sl-empty">
                          <div className="sl-empty-title">No leads found</div>
                          <p className="text-muted mb-2">
                            {allDates
                              ? "No leads match the current filters (offer / branch / search)."
                              : fromDate || toDate
                                ? `Nothing for ${fromDate || "…"} → ${
                                    toDate || "…"
                                  }. Try “All” dates, another offer, or another branch.`
                                : "No leads match the current filters."}
                          </p>
                          {canCreate ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              onClick={openCreate}
                            >
                              + Add first lead
                            </button>
                          ) : null}
                        </div>
                      )}
                    </td>
                  </tr>
                ) : (
                  leads.map((lead) => (
                    <tr key={lead.id} className={rowClass(lead)}>
                      <td>{formatLeadDate(lead.lead_date)}</td>
                      <td>
                        <strong>{lead.customer_name}</strong>
                        <div className="small text-muted">{lead.lead_code}</div>
                      </td>
                      <td>{lead.contact_number}</td>
                      <td>{lead.branch?.name || lead.branch_id}</td>
                      <td>{lead.member_responsible}</td>
                      <td>{lead.contact_medium}</td>
                      <td>{lead.offer}</td>
                      <td className="sl-comments-cell">
                        {commentLines(lead).length ? (
                          <div className="sl-comments">
                            {commentLines(lead).map(([label, text], index) => (
                              <div className="sl-comment-line" title={text} key={`${label}-${index}`}>
                                <span className="sl-comment-label">{label}</span>
                                <span className="sl-comment-text">{text}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                      <td className="sl-progress-cell">
                        <div className="progress sl-progress-bar">
                          <div
                            className="progress-bar bg-success"
                            style={{
                              width: `${calcProgressPct(lead)}%`,
                            }}
                          />
                        </div>
                        <div className="sl-progress-pct">{calcProgressPct(lead)}%</div>
                      </td>
                      <td className="sl-status-cell">
                        <StatusTag lead={lead} />
                      </td>
                      <td className="sl-actions-cell text-nowrap">
                        {canSave ? (
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm me-1"
                            onClick={() => openEdit(lead)}
                          >
                            {isSales && !isAdmin ? "Update" : "Edit"}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => openEdit(lead)}
                          >
                            View
                          </button>
                        )}
                        {canDelete ? (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => removeLead(lead.id)}
                          >
                            Del
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {showForm ? (
            <div className="sl-modal-backdrop">
              <div className="sl-modal card shadow">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <strong>{formTitle}</strong>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowForm(false)}
                  />
                </div>
                <form onSubmit={saveLead}>
                  <div className="card-body">
                    <div className="sl-section-label">Lead intake (Social Media)</div>
                    <div className="row g-2">
                      <div className="col-md-4">
                        <label className="form-label small">Branch *</label>
                        <select
                          className="form-select form-select-sm"
                          value={form.branch_id}
                          disabled={!canEditIntake}
                          onChange={(e) => {
                            const next = e.target.value;
                            setForm({ ...form, branch_id: next });
                            loadOptions(next);
                          }}
                          required={canCreate && !editingId}
                        >
                          <option value="">Select branch</option>
                          {branches.map((b) => (
                            <option
                              key={b.id || b.branch_id}
                              value={b.id || b.branch_id}
                            >
                              {b.name || b.branch_name}
                            </option>
                          ))}
                          {!branches.length && form.branch_id ? (
                            <option value={form.branch_id}>
                              Branch {form.branch_id}
                            </option>
                          ) : null}
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small">Date *</label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={form.lead_date}
                          disabled={!canEditIntake}
                          onChange={(e) =>
                            setForm({ ...form, lead_date: e.target.value })
                          }
                          required={canCreate && !editingId}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small">Customer name *</label>
                        <input
                          className="form-control form-control-sm"
                          value={form.customer_name}
                          disabled={!canEditIntake}
                          onChange={(e) =>
                            setForm({ ...form, customer_name: e.target.value })
                          }
                          required={canCreate && !editingId}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label small">Contact number</label>
                        <input
                          className="form-control form-control-sm"
                          value={form.contact_number}
                          disabled={!canEditIntake}
                          onChange={(e) =>
                            setForm({ ...form, contact_number: e.target.value })
                          }
                        />
                      </div>
                      <div className="col-md-4">
                        <CustomSelect
                          label="Member responsible"
                          value={form.member_responsible}
                          options={options.member_responsible || []}
                          disabled={!canEditAssignment}
                          onChange={setMemberResponsible}
                        />
                      </div>
                      <div className="col-md-4">
                        <CustomSelect
                          label="Contact medium"
                          value={form.contact_medium}
                          options={options.contact_medium || []}
                          disabled={!canEditIntake}
                          onChange={(v) =>
                            setForm({ ...form, contact_medium: v })
                          }
                        />
                      </div>
                      <div className="col-md-4">
                        <CustomSelect
                          label="Offer"
                          value={form.offer}
                          options={options.offer || []}
                          disabled={!canEditIntake}
                          onChange={(v) => setForm({ ...form, offer: v })}
                        />
                      </div>
                      <div className="col-md-8">
                        <label className="form-label small">Comment</label>
                        <textarea
                          className="form-control form-control-sm"
                          rows={2}
                          value={form.comment}
                          disabled={!canEditAssignment}
                          onChange={(e) =>
                            setForm({ ...form, comment: e.target.value })
                          }
                        />
                      </div>
                    </div>

                    <div className="sl-flags mt-2">
                      {INTAKE_FLAGS.map((f) => (
                        <label key={f.key} className="sl-flag">
                          <input
                            type="checkbox"
                            checked={!!form[f.key]}
                            disabled={!canEditIntake}
                            onChange={(e) =>
                              setForm({ ...form, [f.key]: e.target.checked })
                            }
                          />
                          <span>{f.label}</span>
                        </label>
                      ))}
                    </div>

                    <hr className="my-3" />
                    <div className="sl-section-label">
                      Sales follow-up
                      {!canEditSales ? (
                        <span className="text-muted fw-normal">
                          {" "}
                          (view only — sales updates these)
                        </span>
                      ) : null}
                    </div>

                    <div className="sl-flags mt-2">
                      {SALES_FLAGS.map((f) => (
                        <label key={f.key} className="sl-flag">
                          <input
                            type="checkbox"
                            checked={!!form[f.key]}
                            disabled={!canEditSales || (!editingId && !isAdmin)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const next = { ...form, [f.key]: checked };
                              if (f.key === "interested" && checked) {
                                next.not_interested = false;
                              }
                              if (f.key === "not_interested" && checked) {
                                next.interested = false;
                              }
                              if (f.key === "first_call_answered" && checked) {
                                next.call_not_answered = false;
                              }
                              if (f.key === "call_not_answered" && checked) {
                                next.first_call_answered = false;
                              }
                              setForm(next);
                            }}
                          />
                          <span>{f.label}</span>
                        </label>
                      ))}
                    </div>

                    <div className="row g-2 mt-2">
                      <div className="col-md-4">
                        <CustomSelect
                          label="Closed by"
                          value={form.closed_by}
                          options={options.closed_by || []}
                          disabled={!canEditSales || (!editingId && !isAdmin)}
                          onChange={(v) => setForm({ ...form, closed_by: v })}
                        />
                      </div>
                      <div className="col-md-8">
                        <label className="form-label small">
                          Follow up comment
                        </label>
                        <textarea
                          className="form-control form-control-sm"
                          rows={2}
                          value={form.follow_up_comment}
                          disabled={!canEditSales || (!editingId && !isAdmin)}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              follow_up_comment: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small">1st follow up — what you did</label>
                        <textarea
                          className="form-control form-control-sm"
                          rows={2}
                          value={form.first_follow_up_note}
                          disabled={!canEditSales || (!editingId && !isAdmin)}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              first_follow_up_note: e.target.value,
                              follow_up_calls: e.target.value.trim() ? true : form.follow_up_calls,
                            })
                          }
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small">1st follow up — their response</label>
                        <textarea
                          className="form-control form-control-sm"
                          rows={2}
                          value={form.first_follow_up_response}
                          disabled={!canEditSales || (!editingId && !isAdmin)}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              first_follow_up_response: e.target.value,
                              follow_up_calls: e.target.value.trim() ? true : form.follow_up_calls,
                            })
                          }
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small">2nd follow up — what you did</label>
                        <textarea
                          className="form-control form-control-sm"
                          rows={2}
                          value={form.second_follow_up_note}
                          disabled={!canEditSales || (!editingId && !isAdmin)}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              second_follow_up_note: e.target.value,
                              second_follow_up: e.target.value.trim() ? true : form.second_follow_up,
                            })
                          }
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label small">2nd follow up — their response</label>
                        <textarea
                          className="form-control form-control-sm"
                          rows={2}
                          value={form.second_follow_up_response}
                          disabled={!canEditSales || (!editingId && !isAdmin)}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              second_follow_up_response: e.target.value,
                              second_follow_up: e.target.value.trim() ? true : form.second_follow_up,
                            })
                          }
                        />
                      </div>
                    </div>

                    <div className="mt-3 mb-1">
                      <div className="d-flex justify-content-between small">
                        <span>
                          Implementation
                          {form.payment_received ? (
                            <span className="text-success ms-1">(Closed — complete)</span>
                          ) : null}
                        </span>
                        <span>{progressPct}%</span>
                      </div>
                      <div className="progress" style={{ height: 10 }}>
                        <div
                          className={`progress-bar ${
                            form.payment_received ? "bg-success" : "bg-success"
                          }`}
                          style={{ width: `${progressPct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="card-footer d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowForm(false)}
                    >
                      Close
                    </button>
                    {canSave && (editingId || canCreate) ? (
                      <button
                        type="submit"
                        className="btn btn-sm btn-success"
                        disabled={saving}
                      >
                        {saving ? "Saving…" : "Save"}
                      </button>
                    ) : null}
                  </div>
                </form>
              </div>
            </div>
          ) : null}
          </div>
        </div>
      </main>
    </div>
  );
}
